import React, { useEffect, useState } from 'react';
import { X, MailWarning, RotateCcw, Check, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const BounceManager = ({ open, onClose, onChanged }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [bounces, setBounces] = useState([]);
  const [edits, setEdits] = useState({}); // email -> new email
  const [busy, setBusy] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/crm/bounces').then((r) => setBounces(r.data.bounces || [])).catch(() => setBounces([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const restore = async (email) => {
    setBusy(email);
    try {
      const r = await api.post('/admin/crm/bounces/restore', { email });
      toast({ title: r.data.message });
      setBounces((b) => b.filter((x) => x.email !== email));
      onChanged && onChanged();
    } catch { toast({ title: 'Could not restore', variant: 'destructive' }); }
    finally { setBusy(''); }
  };

  const correct = async (email) => {
    const next = (edits[email] || '').trim();
    if (!next || !next.includes('@')) { toast({ title: 'Enter a valid new email', variant: 'destructive' }); return; }
    setBusy(email);
    try {
      const r = await api.post('/admin/crm/bounces/correct', { old_email: email, new_email: next });
      toast({ title: r.data.message });
      setBounces((b) => b.filter((x) => x.email !== email));
      onChanged && onChanged();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not correct', variant: 'destructive' }); }
    finally { setBusy(''); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bounce-manager">
      <div className="bg-cream rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col border border-rose/40">
        <div className="flex items-center justify-between px-6 py-4 border-b border-rose/30">
          <h3 className="font-serif-display text-xl text-burgundy inline-flex items-center gap-2"><MailWarning className="w-5 h-5 text-rose-italic" /> Bounced emails</h3>
          <button onClick={onClose} data-testid="bounce-close"><X className="w-5 h-5 text-mahogany/60" /></button>
        </div>
        <div className="px-6 py-3 text-sm text-mahogany/70">These addresses are undeliverable and skipped by campaigns. Correct a typo to fix and re-add it, or restore it as-is.</div>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>
          ) : bounces.length === 0 ? (
            <div className="text-center text-mahogany/60 py-10" data-testid="bounce-empty">No bounced emails 🎉</div>
          ) : bounces.map((b) => (
            <div key={b.email} data-testid={`bounce-row-${b.email}`} className="py-3 border-b border-rose/20">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-burgundy font-medium truncate">{b.email}</div>
                  <div className="text-xs text-mahogany/60">{b.reason || b.event} {b.bounced_at ? `· ${new Date(b.bounced_at).toLocaleDateString()}` : ''}</div>
                </div>
                <button onClick={() => restore(b.email)} disabled={busy === b.email} data-testid={`bounce-restore-${b.email}`}
                  className="pill-btn-outline text-xs inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Restore as-is</button>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <input type="email" placeholder="corrected@email.com" value={edits[b.email] || ''}
                  onChange={(e) => setEdits((s) => ({ ...s, [b.email]: e.target.value }))} data-testid={`bounce-input-${b.email}`}
                  className="flex-1 min-w-[200px] rounded-full border border-rose/50 bg-white/70 px-4 py-1.5 text-sm text-burgundy focus:outline-none" />
                <button onClick={() => correct(b.email)} disabled={busy === b.email} data-testid={`bounce-correct-${b.email}`}
                  className="pill-btn text-xs inline-flex items-center gap-1.5">
                  {busy === b.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save & re-add
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BounceManager;
